import React, { useState, useEffect } from 'react';
import { FaChartBar, FaMusic, FaCompactDisc, FaClock, FaHeadphones, FaTrash, FaChevronLeft } from 'react-icons/fa';
import axios from 'axios';
import '../styles/StatsPage.css';

const API = process.env.REACT_APP_API_URL || '';

function StatsPage() {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('stats');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/history/stats`),
        axios.get(`${API}/api/history/recent?limit=50`)
      ]);
      setStats(statsRes.data);
      setHistory(historyRes.data);
    } catch (e) {
      console.error('Erreur chargement stats:', e);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = async () => {
    if (!window.confirm('Supprimer tout l\'historique ?')) return;
    try {
      await axios.delete(`${API}/api/history/clear`);
      setHistory([]);
      loadData();
    } catch (e) { console.error(e); }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0min';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}min`;
    return `${m}min`;
  };

  const formatDate = (d) => {
    const date = new Date(d);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'À l\'instant';
    if (diff < 3600000) return `Il y a ${Math.floor(diff/60000)}min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff/3600000)}h`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="stats-loading"><FaChartBar className="spin" /> Chargement...</div>;

  const maxDaily = stats?.dailyListens?.length ? Math.max(...stats.dailyListens.map(d => d.count)) : 1;
  const maxHourly = stats?.hourlyStats?.length ? Math.max(...stats.hourlyStats.map(h => h.count)) : 1;

  return (
    <div className="stats-page">
      <div className="stats-header">
        <h1><FaChartBar /> Mes Statistiques</h1>
        <div className="stats-tabs">
          <button className={tab === 'stats' ? 'active' : ''} onClick={() => setTab('stats')}>Statistiques</button>
          <button className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>Historique</button>
        </div>
      </div>

      {tab === 'stats' && stats && (
        <div className="stats-content">
          {/* Cartes résumé */}
          <div className="stats-cards">
            <div className="stat-card">
              <FaHeadphones className="stat-icon" />
              <div className="stat-value">{stats.totals.total_listens}</div>
              <div className="stat-label">Écoutes totales</div>
            </div>
            <div className="stat-card">
              <FaClock className="stat-icon" />
              <div className="stat-value">{formatDuration(stats.totals.total_seconds)}</div>
              <div className="stat-label">Temps d'écoute</div>
            </div>
            <div className="stat-card">
              <FaMusic className="stat-icon" />
              <div className="stat-value">{stats.totals.unique_tracks}</div>
              <div className="stat-label">Pistes uniques</div>
            </div>
            <div className="stat-card">
              <FaCompactDisc className="stat-icon" />
              <div className="stat-value">{stats.totals.unique_albums}</div>
              <div className="stat-label">Albums écoutés</div>
            </div>
          </div>

          {/* Top Pistes */}
          {stats.topTracks.length > 0 && (
            <div className="stats-section">
              <h2>🎵 Top Pistes</h2>
              <div className="top-list">
                {stats.topTracks.map((t, i) => (
                  <div key={t.id} className="top-item">
                    <span className="top-rank">#{i + 1}</span>
                    <img src={t.cover_url ? (t.cover_url.startsWith('http') ? t.cover_url : `${API}${t.cover_url}`) : '/logo192.png'} alt="" className="top-cover" />
                    <div className="top-info">
                      <div className="top-title">{t.title}</div>
                      <div className="top-subtitle">{t.album_title}</div>
                    </div>
                    <span className="top-count">{t.play_count} écoutes</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Top Albums */}
          {stats.topAlbums.length > 0 && (
            <div className="stats-section">
              <h2>💿 Top Albums</h2>
              <div className="top-list">
                {stats.topAlbums.map((a, i) => (
                  <div key={a.id} className="top-item">
                    <span className="top-rank">#{i + 1}</span>
                    <img src={a.cover_url ? (a.cover_url.startsWith('http') ? a.cover_url : `${API}${a.cover_url}`) : '/logo192.png'} alt="" className="top-cover" />
                    <div className="top-info">
                      <div className="top-title">{a.title}</div>
                      <div className="top-subtitle">{a.artist}</div>
                    </div>
                    <span className="top-count">{a.play_count} écoutes</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Graphique 30 jours */}
          {stats.dailyListens.length > 0 && (
            <div className="stats-section">
              <h2>📊 30 derniers jours</h2>
              <div className="chart-container">
                <div className="bar-chart">
                  {stats.dailyListens.map((d, i) => (
                    <div key={i} className="bar-wrapper" title={`${new Date(d.date).toLocaleDateString('fr-FR')}: ${d.count} écoutes`}>
                      <div className="bar" style={{ height: `${(d.count / maxDaily) * 100}%` }} />
                      {i % 5 === 0 && <span className="bar-label">{new Date(d.date).getDate()}</span>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Heures d'écoute */}
          {stats.hourlyStats.length > 0 && (
            <div className="stats-section">
              <h2>🕐 Heures d'écoute préférées</h2>
              <div className="chart-container">
                <div className="bar-chart hourly">
                  {Array.from({ length: 24 }, (_, h) => {
                    const data = stats.hourlyStats.find(s => s.hour === h);
                    const count = data?.count || 0;
                    return (
                      <div key={h} className="bar-wrapper" title={`${h}h: ${count} écoutes`}>
                        <div className="bar" style={{ height: `${(count / maxHourly) * 100}%` }} />
                        {h % 3 === 0 && <span className="bar-label">{h}h</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Catégories */}
          {stats.topCategories.length > 0 && (
            <div className="stats-section">
              <h2>📁 Par catégorie</h2>
              <div className="category-bars">
                {stats.topCategories.map(c => {
                  const total = stats.topCategories.reduce((s, x) => s + x.play_count, 0);
                  const pct = ((c.play_count / total) * 100).toFixed(0);
                  const labels = { film_soundtrack: 'Films', disney_park: 'Parcs', public_album: 'Albums publics' };
                  return (
                    <div key={c.category} className="cat-bar-row">
                      <span className="cat-label">{labels[c.category] || c.category}</span>
                      <div className="cat-bar-bg">
                        <div className="cat-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="cat-pct">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'history' && (
        <div className="history-content">
          <div className="history-header">
            <h2>Récemment écoutés</h2>
            {history.length > 0 && (
              <button className="clear-history-btn" onClick={clearHistory}><FaTrash /> Effacer</button>
            )}
          </div>
          {history.length === 0 ? (
            <div className="empty-state">Aucun historique d'écoute</div>
          ) : (
            <div className="history-list">
              {history.map((h, i) => (
                <div key={i} className="history-item">
                  <img src={h.cover_url ? (h.cover_url.startsWith('http') ? h.cover_url : `${API}${h.cover_url}`) : '/logo192.png'} alt="" className="history-cover" />
                  <div className="history-info">
                    <div className="history-title">{h.track_title}</div>
                    <div className="history-subtitle">{h.album_title} • {h.track_artist || h.album_artist}</div>
                  </div>
                  <div className="history-meta">
                    <span className="history-time">{formatDate(h.listened_at)}</span>
                    {h.completed ? <span className="history-completed">✓</span> : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default StatsPage;
